import { useEffect, useRef } from "react";

import useAxiosPrivate from "@/api/hooks/useAxiosPrivate";
import { ApiResources } from "@/api/resources";
import LayoutWrapper from "@/components/Layout/LayoutWrapper";
import IntegrationsApiHeaderActions from "@/components/PageHeader/actions/pageHeaderActions/IntegrationsApiHeaderActions/IntegrationsApiHeaderActions";
import TabFilters from "@/components/TabFilters/TabFilters";
import DisableIntegrationDialog from "@/components/Table/actions/dialogs/Integrations/DisableIntegrationDialog/DisableIntegrationDialog";
import EditApiKeyDialog from "@/components/Table/actions/dialogs/IntegrationsApi/EditApiKeyDialog";
import useIntegrationsApiInlineActions from "@/components/Table/actions/inlineActions/hooks/useIntegrationsApiInlineActions";
import useTableInlineActions from "@/components/Table/hooks/useTableInlineActions";
import Table from "@/components/Table/Table";
import { USER_PERMISSIONS } from "@/roles/types/enums";
import { ROUTES, TABLE_NAMES } from "@/shared";
import useResponse from "@/shared/hooks/useResponse";
import { useIntegrationsApiTableStore } from "@/stores/useTableStore";
import { getAccessStatus } from "@/stores/useUserDataStore";

import {
  INTEGRATIONS_PAGE_TABS,
  INTEGRATIONS_PAGE_TABS_PARTIAL
} from "../config";
import { useDisableIntegration } from "../hooks/useDisableIntegration";
import IntegrationsDialogHub from "../IntegrationsDialogHub";

const IntegrationsApi = () => {
  const { getData } = useAxiosPrivate();
  const { handleResponse } = useResponse();
  const { fetchData, resetInitialLoading, setIsCheckable, setInlineActions } =
    useIntegrationsApiTableStore();

  const {
    switchData,
    isDisableDialogLoading,
    isDisableDialogOpen,
    handleDisableClose,
    handleSubmitDisable
  } = useDisableIntegration(
    ROUTES.IntegrationsApi,
    ApiResources.IntegrationsApiCredentialsEnabled
  );

  const canViewFullTabs = getAccessStatus(
    USER_PERMISSIONS.SeeAnalysisProviders
  );

  const { inlineActions, rowData, isEditOpen, closeEditDialog } =
    useIntegrationsApiInlineActions();

  const { tableInlineActions, allowedToManageData: allowedToManageApiKeys } =
    useTableInlineActions(inlineActions, USER_PERMISSIONS.ManageApiCredentials);

  const hasFetched = useRef(false);

  useEffect(() => {
    updateIntegrationsApiActions();
  }, [tableInlineActions]);

  useEffect(() => {
    if (!hasFetched.current) {
      hasFetched.current = true;
      resetInitialLoading();
      handlePageLoad();
    }
  }, []);

  const updateIntegrationsApiActions = () => {
    setInlineActions(tableInlineActions);

    setIsCheckable(allowedToManageApiKeys);
  };

  const handlePageLoad = async () => {
    updateIntegrationsApiActions();
    await fetchData(getData, handleResponse);
  };

  const tableTabs = !canViewFullTabs
    ? INTEGRATIONS_PAGE_TABS_PARTIAL
    : INTEGRATIONS_PAGE_TABS;

  return (
    <>
      <LayoutWrapper
        showDetailsSection={false}
        route={ROUTES.IntegrationsApi}
        tableName={TABLE_NAMES.Integrations}
      >
        {allowedToManageApiKeys && <IntegrationsApiHeaderActions />}
      </LayoutWrapper>

      <Table
        route={ROUTES.IntegrationsApi}
        hasDetails={false}
        tabFilters={<TabFilters tabs={tableTabs} />}
        hasTabFilters
      />

      <IntegrationsDialogHub />

      {rowData && (
        <EditApiKeyDialog
          isOpen={isEditOpen}
          onClose={closeEditDialog}
          rowData={rowData}
        />
      )}

      {switchData && (
        <DisableIntegrationDialog
          rowData={switchData}
          isLoading={isDisableDialogLoading}
          isOpen={isDisableDialogOpen}
          handleDisableSubmit={handleSubmitDisable}
          handleClose={handleDisableClose}
        />
      )}
    </>
  );
};

export default IntegrationsApi;
